import { TFunction } from "i18next";
import {
  CustomFormPageFieldUuidToFileEntitiesMap,
  FileEntity,
  FileEntityUuidToFileMap,
  ToastService,
  wait
} from '@flicent/fieldpulse-react-component-library-frontend';
import { PreloaderProgress } from '@flicent/fieldpulse-react-component-library-frontend/dist/providers/PreloaderContextProvider/PreloaderContextProvider.props';
import { ParentCustomFormSelectedFileUploadResponse } from '@/interfaces';
import { createFileResponseToFileEntity } from './create-file-response-to-file-entity.util';

const MAX_UPLOAD_FILES_BATCH_SIZE_BYTES_MOBILE: number = 5 * 1024 * 1024; // 5 MB
const MAX_UPLOAD_FILES_BATCH_SIZE_BYTES_DESKTOP: number = 50 * 1024 * 1024; // 50 MB

interface FileUploadChunk {
  pageFieldUuid: string;
  fileEntity: FileEntity;
  file: File | null;
}

interface FilesOperationsResult {
  created: FileUploadChunk[];
  modified: FileEntity[];
  deleted: number[];
}

const gatherFilesUploadChunks = (
  initialUuidToFileEntityMap: CustomFormPageFieldUuidToFileEntitiesMap,
  uuidToFileEntityMap: CustomFormPageFieldUuidToFileEntitiesMap,
  uuidToFileMap: FileEntityUuidToFileMap,
): FilesOperationsResult => {
  const createdChunks: FileUploadChunk[] = [];
  const modifiedFileEntities: FileEntity[] = [];
  const deletedFileEntitiesIds: number[] = [];

  // Define Modified and Deleted
  for (const fieldUuid in initialUuidToFileEntityMap) {
    const initial: FileEntity[] = initialUuidToFileEntityMap[fieldUuid];
    const current: FileEntity[] = uuidToFileEntityMap[fieldUuid] || [];

    const modified: FileEntity[] = current.filter((currentFileEntity) => {
      const initialFileEntity: FileEntity | undefined = initial.find((initialFileEntity) => initialFileEntity.id === currentFileEntity.id);
      if (!initialFileEntity) {
        return false;
      }

      return JSON.stringify(currentFileEntity) !== JSON.stringify(initialFileEntity);
    });
    modifiedFileEntities.push(...modified);

    const initialIds: number[] = initial.map((fileEntity) => fileEntity.id);
    const currentIds: number[] = current.map((fileEntity) => fileEntity.id);
    const deletedIds: number[] = initialIds.filter((id) => !currentIds.includes(id))
    deletedFileEntitiesIds.push(...deletedIds);
  }

  // Define Created
  for (const fieldUuid in uuidToFileEntityMap) {
    const initialFileEntities: FileEntity[] = initialUuidToFileEntityMap[fieldUuid] || [];
    const initialFileEntitiesIds: number[] = initialFileEntities.map((fileEntity) => fileEntity.id);
    const createdFileEntities: FileEntity[] = uuidToFileEntityMap[fieldUuid].filter((fileEntity) => !initialFileEntitiesIds.includes(fileEntity.id));

    for (const fileEntity of createdFileEntities) {
      const file = uuidToFileMap[fileEntity.uuid] || null;
      createdChunks.push({
        pageFieldUuid: fieldUuid,
        fileEntity,
        file,
      });
    }
  }

  return {
    created: createdChunks,
    modified: modifiedFileEntities,
    deleted: deletedFileEntitiesIds,
  };
}

export interface UploadCustomFormFieldsFilesUpdatesReturnType {
  allFailed: boolean;
  somethingFailed: boolean;
  failedToCreateFileEntitiesUuids: string[];
  newInitialPageFieldUuidToFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap;
  newCurrentPageFieldUuidToFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap;
  newCurrentFileEntityUuidToFileMap: FileEntityUuidToFileMap;
}

interface CreateFileFunctionParams {
  pageFieldUuid: string;
  fileEntity: FileEntity;
  file: File;
}

interface CreateFileWithParentFunctionParams {
  pageFieldUuid: string;
  fileEntity: FileEntity;
}

interface CreateFilesFunctionReturnType extends FileEntity {
}

interface CreateFileFunction {
  (params: CreateFileFunctionParams, onProgress: (progress: number) => void): Promise<CreateFilesFunctionReturnType>,
}

export interface CreateFileWithParentProgressFunctionParam {
  (fieldUuid: string, progress: number): void
}

interface CreateFileWithParentWindowFunction {
  (chunks: CreateFileWithParentFunctionParams[], onProgress: CreateFileWithParentProgressFunctionParam): Promise<{ [fieldEntityUuid in string]: ParentCustomFormSelectedFileUploadResponse }>,
}

interface ModifyFilesFunction {
  (params: FileEntity[]): Promise<void>,
}

interface DeleteFilesFunction {
  (params: number[]): Promise<void>,
}


export const uploadCustomFormFieldsFilesUpdates = async (
  t: TFunction,
  useParentWindowToCreateFile: boolean,
  isMobileOrTablet: boolean,
  initialUuidToFileEntityMap: CustomFormPageFieldUuidToFileEntitiesMap,
  uuidToFileEntityMap: CustomFormPageFieldUuidToFileEntitiesMap,
  uuidToFileMap: FileEntityUuidToFileMap,
  createFileWithParentWindow: CreateFileWithParentWindowFunction,
  createFileFunction: CreateFileFunction,
  modifyFilesFunction: ModifyFilesFunction,
  deleteFilesFunction: DeleteFilesFunction,
  onProgress?: (progressPercent: PreloaderProgress) => void,
): Promise<UploadCustomFormFieldsFilesUpdatesReturnType> => {
  const {
    created,
    modified,
    deleted
  }: FilesOperationsResult = gatherFilesUploadChunks(initialUuidToFileEntityMap, uuidToFileEntityMap, uuidToFileMap);

  const successfullyCreatedChunks: FileUploadChunk[] = [];
  const failedCreatedChunks: FileUploadChunk[] = [];
  const createdFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap = {};

  let createdErrors: string[] = [];
  let modifiedError: string | undefined;
  let deletedError: string | undefined;

  let createPromises: Promise<void>[] = [];
  let modifyPromise: Promise<void> | undefined;
  let deletePromise: Promise<void> | undefined;

  if (created.length) {
    let progressPerFile: Record<string, number> = {};
    const fullProgress: number = created.length * 100;

    const onFileProgress = (fileUuid: string, progressPercent: number) => {
      progressPerFile[fileUuid] = progressPercent;

      const progressRawNumber: number = Object.values(progressPerFile).reduce((filesProgressesSum, fileProgress) => filesProgressesSum + fileProgress, 0) / fullProgress * 100;
      const progress = parseInt((progressRawNumber).toFixed(0), 10);

      onProgress?.({ progress, label: t('fileUpload.uploadingFiles') });
    };

    const onFileFail = (chunk: FileUploadChunk, errorMessage: string) => {
      failedCreatedChunks.push(chunk);
      createdErrors.push(t('fileUpload.failedToCreate', { name: chunk.fileEntity.display_name, error: errorMessage }));
    };

    if (useParentWindowToCreateFile) {
      createPromises.push(createFileWithParentWindow(created, onFileProgress).then((uploadResponses) => {
        for (const chunk of created) {
          const { pageFieldUuid } = chunk;
          if (!uploadResponses[pageFieldUuid]) {
            continue;
          }

          const { error, error_message, response } = uploadResponses[pageFieldUuid];

          if (error) {
            onFileFail(chunk, error_message || t('common.unknownError'));
          } else {
            if (!createdFileEntitiesMap[pageFieldUuid]) {
              createdFileEntitiesMap[pageFieldUuid] = [];
            }
            createdFileEntitiesMap[pageFieldUuid].push(createFileResponseToFileEntity(pageFieldUuid, response!));
            successfullyCreatedChunks.push(chunk);
          }
        }
      }));
    } else {
      let sizeBytesAccumulator: number = 0;
      const sizeLimit: number = isMobileOrTablet ? MAX_UPLOAD_FILES_BATCH_SIZE_BYTES_MOBILE : MAX_UPLOAD_FILES_BATCH_SIZE_BYTES_DESKTOP;

      for (const chunk of created) {
        // Upload files in batches to avoid network overload
        const fileSize: number = chunk.fileEntity.size_bytes;
        const isNewSizeExceeding: boolean = sizeBytesAccumulator + fileSize > sizeLimit;
        if (isNewSizeExceeding) {
          await Promise.all(createPromises);
          sizeBytesAccumulator = fileSize;
        } else {
          sizeBytesAccumulator += fileSize;
        }

        const chunkFile: File | null = chunk.file;
        if (!chunkFile) {
          onFileFail(chunk, 'File is missing');
          continue;
        }

        const createPromise = createFileFunction({ ...chunk, file: chunkFile }, (progress) => {
          onFileProgress(chunk.fileEntity.uuid, progress);
        }).then((fileEntity) => {
          const { pageFieldUuid } = chunk;

          if (!createdFileEntitiesMap[pageFieldUuid]) {
            createdFileEntitiesMap[pageFieldUuid] = [];
          }
          createdFileEntitiesMap[pageFieldUuid].push(fileEntity);

          successfullyCreatedChunks.push(chunk);
        }).catch((error: any) => {
          onFileFail(chunk, error.message);
        });

        createPromises.push(createPromise);
      }
    }
  }

  if (modified.length) {
    modifyPromise = modifyFilesFunction(modified).catch((error: any) => {
      modifiedError = t('fileUpload.failedToModify', { count: modified.length, error: error.message });
    });
  }

  if (deleted.length) {
    deletePromise = deleteFilesFunction(deleted).catch((error: any) => {
      deletedError = t('fileUpload.failedToDelete', { count: deleted.length, error: error.message });
    });
  }

  await Promise.all([
    ...createPromises,
    modifyPromise,
    deletePromise,
  ]);

  if (createdErrors.length > 0 || modifiedError || deletedError) {
    const errorMessage: string = [
      ...createdErrors,
      modifiedError ? modifiedError : '',
      deletedError ? deletedError : '',
    ].filter(Boolean).join('\n');

    ToastService.middleScreenErrorToast(errorMessage);
    // Wait for 2 seconds so that the user can read the error message
    await wait(2250);
  }

  const newInitialPageFieldUuidToFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap = initialUuidToFileEntityMap;

  if (!modifiedError) {
    // If there were no errors in modification, we can update the initial map with modified files
    for (const fieldUuid in newInitialPageFieldUuidToFileEntitiesMap) {
      newInitialPageFieldUuidToFileEntitiesMap[fieldUuid] = newInitialPageFieldUuidToFileEntitiesMap[fieldUuid].map((fileEntity) => {
        const modifiedFileEntity: FileEntity | undefined = modified.find((modifiedFile) => modifiedFile.id === fileEntity.id);
        return modifiedFileEntity ? modifiedFileEntity : fileEntity;
      })
    }
  }

  if (!deletedError) {
    // If there were no errors in deletion, we can update the initial map with deleted files
    for (const fieldUuid in newInitialPageFieldUuidToFileEntitiesMap) {
      newInitialPageFieldUuidToFileEntitiesMap[fieldUuid] = newInitialPageFieldUuidToFileEntitiesMap[fieldUuid].filter((fileEntity) => {
        return !deleted.includes(fileEntity.id);
      });
    }
  }

  // Gather all failed created file entities UUIDs
  const failedToCreateFileEntitiesUuids: string[] = [];
  for (const failedChunk of failedCreatedChunks) {
    failedToCreateFileEntitiesUuids.push(failedChunk.fileEntity.uuid);
  }

  // If there are created files, we can update the initial map with created files
  for (const fieldUuid in createdFileEntitiesMap) {
    if (!newInitialPageFieldUuidToFileEntitiesMap[fieldUuid]) {
      newInitialPageFieldUuidToFileEntitiesMap[fieldUuid] = [];
    }
    newInitialPageFieldUuidToFileEntitiesMap[fieldUuid].push(...createdFileEntitiesMap[fieldUuid]);
  }

  const newCurrentFileEntityUuidToFileMap: FileEntityUuidToFileMap = {};
  for (const chunk of failedCreatedChunks) {
    if (chunk.file) {
      newCurrentFileEntityUuidToFileMap[chunk.fileEntity.uuid] = chunk.file;
    }
  }

  const newCurrentPageFieldUuidToFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap = uuidToFileEntityMap;

  if (!modifiedError) {
    // If there were no errors in modification, we can update the current map with modified files
    for (const fieldUuid in newCurrentPageFieldUuidToFileEntitiesMap) {
      newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid] = newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid].map((fileEntity) => {
        const modifiedFileEntity: FileEntity | undefined = modified.find((modifiedFile) => modifiedFile.id === fileEntity.id);
        return modifiedFileEntity ? modifiedFileEntity : fileEntity;
      });
    }
  }

  if (!deletedError) {
    // If there were no errors in deletion, we can update the current map with deleted files
    for (const fieldUuid in newCurrentPageFieldUuidToFileEntitiesMap) {
      newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid] = newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid].filter((fileEntity) => {
        return !deleted.includes(fileEntity.id);
      });
    }
  }

  // Delete the TEMP files that were successfully created from the current map
  const filesUuids: string[] = successfullyCreatedChunks.map((chunk) => chunk.fileEntity.uuid);
  for (const fieldUuid in newCurrentPageFieldUuidToFileEntitiesMap) {
    newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid] = newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid].filter((fileEntity) => {
      return !filesUuids.includes(fileEntity.uuid);
    });
  }

  // Push the created files to the current map
  for (const fieldUuid in createdFileEntitiesMap) {
    if (!newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid]) {
      newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid] = [];
    }
    newCurrentPageFieldUuidToFileEntitiesMap[fieldUuid].push(...createdFileEntitiesMap[fieldUuid]);
  }

  return {
    somethingFailed: !!modifiedError || !!deletedError || failedCreatedChunks.length > 0,
    allFailed: !!modifiedError && !!deletedError && failedCreatedChunks.length === created.length,
    failedToCreateFileEntitiesUuids,

    newInitialPageFieldUuidToFileEntitiesMap,

    newCurrentPageFieldUuidToFileEntitiesMap,
    newCurrentFileEntityUuidToFileMap: newCurrentFileEntityUuidToFileMap,
  };
};